import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@/shared/i18n/useT';
import { useBookingT, type BookingMessageKey } from './i18n';
import { bookingErrorKey, fetchAvailability } from './api';
import type { SlotCapacity } from './types';
import { ErrorNotice } from './ErrorNotice';
import { MonthCalendar } from './MonthCalendar';
import { TimezoneSelect } from './TimezoneSelect';
import {
  addMonths,
  dayKeyInZone,
  groupSlotsByDay,
  monthKeyInZone,
  monthRangeUtc,
  type DayKey,
  type MonthKey,
} from './datetime';

interface Props {
  slug: string;
  eventTypeId: string;
  professionalZone: string;
  visitorZone: string;
  /** Display zone. Controlled, because the URL carries it. */
  timeZone: string;
  onTimeZoneChange: (timeZone: string) => void;
  month: MonthKey;
  onMonthChange: (month: MonthKey) => void;
  selectedDay: DayKey | null;
  onSelectDay: (day: DayKey | null) => void;
  selectedSlot: string | null;
  onSelectSlot: (slot: string) => void;
  /** How far ahead the event type accepts bookings. */
  horizonDays?: number;
  /** Bump to force a refetch — the 409 path uses it to reload stale slots. */
  refreshToken?: number;
}

type LoadState = 'idle' | 'loading' | 'error';

/**
 * Calendar + timezone + time column, in one place.
 *
 * Both the first booking and a reschedule pick a slot the same way, so they
 * share this component rather than two drifting copies. It owns the
 * availability request; the surrounding screen owns the selection, because on
 * the public page that selection lives in the URL.
 */
export function SlotPicker({
  slug,
  eventTypeId,
  professionalZone,
  visitorZone,
  timeZone,
  onTimeZoneChange,
  month,
  onMonthChange,
  selectedDay,
  onSelectDay,
  selectedSlot,
  onSelectSlot,
  horizonDays = 60,
  refreshToken = 0,
}: Props) {
  const t = useBookingT();
  const { formatLocale } = useI18n();
  const [slots, setSlots] = useState<readonly string[]>([]);
  const [capacities, setCapacities] = useState<Map<string, SlotCapacity>>(new Map());
  const [state, setState] = useState<LoadState>('idle');
  const [errorKey, setErrorKey] = useState<BookingMessageKey>(
    'calendarUi.availabilityError',
  );
  const requestId = useRef(0);

  const now = useMemo(() => new Date(), []);
  const minMonth = monthKeyInZone(now, timeZone);
  const maxMonth = monthKeyInZone(
    new Date(now.getTime() + horizonDays * 86_400_000),
    timeZone,
  );
  const today = dayKeyInZone(now, timeZone);

  const load = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    setState('loading');
    const range = monthRangeUtc(month, timeZone);
    // The Worker rejects a range that ends in the past; an entirely past month
    // simply has nothing to offer.
    const from = range.from.getTime() < Date.now() ? new Date() : range.from;
    if (range.to.getTime() <= from.getTime()) {
      if (requestId.current === id) {
        setSlots([]);
        setCapacities(new Map());
        setState('idle');
      }
      return;
    }
    try {
      const availability = await fetchAvailability(
        slug,
        eventTypeId,
        from,
        range.to,
      );
      if (requestId.current !== id) return;
      setSlots(availability.slots);
      const capMap = new Map<string, SlotCapacity>();
      if (availability.slotCapacities) {
        for (const cap of availability.slotCapacities) {
          capMap.set(cap.slot, cap);
        }
      }
      setCapacities(capMap);
      setState('idle');
    } catch (error) {
      if (requestId.current !== id) return;
      setSlots([]);
      setCapacities(new Map());
      setErrorKey(bookingErrorKey(error));
      setState('error');
    }
  }, [eventTypeId, month, slug, timeZone]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const byDay = useMemo(() => groupSlotsByDay(slots, timeZone), [slots, timeZone]);
  const availableDays = useMemo(() => new Set(byDay.keys()), [byDay]);
  const daySlots = selectedDay ? (byDay.get(selectedDay) ?? []) : [];

  // A day chosen in one zone can cease to exist in another; drop it rather
  // than leaving a selection that maps to nothing.
  useEffect(() => {
    if (selectedDay && slots.length > 0 && !availableDays.has(selectedDay)) {
      onSelectDay(null);
    }
  }, [availableDays, onSelectDay, selectedDay, slots.length]);

  const timeFormat = useMemo(
    () => new Intl.DateTimeFormat(formatLocale, { timeStyle: 'short', timeZone }),
    [formatLocale, timeZone],
  );
  const dayFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(formatLocale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }),
    [formatLocale],
  );

  const loading = state === 'loading';

  return (
    <div className="flex flex-col gap-4">
      <TimezoneSelect
        value={timeZone}
        visitorZone={visitorZone}
        professionalZone={professionalZone}
        onChange={onTimeZoneChange}
      />

      {state === 'error' && (
        <ErrorNotice
          message={t(errorKey)}
          action={
            <button
              type="button"
              onClick={() => void load()}
              className="tap-target rounded-full border border-border-strong px-3 text-sm"
            >
              {t('common.retry')}
            </button>
          }
        />
      )}

      <MonthCalendar
        month={month}
        availableDays={availableDays}
        selectedDay={selectedDay}
        today={today}
        minMonth={minMonth < month ? minMonth : month}
        maxMonth={maxMonth > month ? maxMonth : month}
        loading={loading}
        onSelectDay={onSelectDay}
        onMonthChange={(next) => {
          onSelectDay(null);
          onMonthChange(next);
        }}
      />

      <p className="text-xs text-muted">
        {t('publicBooking.timezone.shownIn', { timezone: timeZone })}
      </p>

      <div aria-live="polite" className="min-h-11">
        {loading && (
          <p role="status" className="text-sm text-muted">
            {t('calendarUi.slotsLoading')}
          </p>
        )}

        {!loading && state !== 'error' && availableDays.size === 0 && (
          <p className="rounded-lg border border-border bg-surface-3 px-3 py-3 text-sm text-muted">
            {t('publicBooking.calendar.noSlotsThisMonth')}{' '}
            <button
              type="button"
              className="underline"
              onClick={() => {
                onSelectDay(null);
                onMonthChange(addMonths(month, 1));
              }}
            >
              {t('publicBooking.calendar.nextMonth')}
            </button>
          </p>
        )}

        {!loading && availableDays.size > 0 && !selectedDay && (
          <p className="text-sm text-muted">
            {t('publicBooking.calendar.pickADay')}
          </p>
        )}

        {!loading && selectedDay && daySlots.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-fg">
              {t('publicBooking.calendar.timesOn', {
                date: dayFormat.format(new Date(`${selectedDay}T00:00:00Z`)),
              })}
            </h3>
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {daySlots.map((slot) => {
                const isSelected = slot === selectedSlot;
                const capacity = capacities.get(slot);
                const isSoldOut = capacity?.isFull === true || capacity?.remainingCapacity === 0;
                const badge = capacity?.badge;

                return (
                  <li key={slot}>
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      disabled={isSoldOut}
                      onClick={() => !isSoldOut && onSelectSlot(slot)}
                      className={`tap-target flex flex-col items-center justify-center py-1.5 px-2 w-full rounded-2xl border text-xs transition-colors ${
                        isSelected
                          ? 'border-accent bg-accent font-semibold text-accent-fg'
                          : isSoldOut
                            ? 'border-border bg-surface-3 text-muted opacity-60 cursor-not-allowed'
                            : 'border-border-strong text-fg hover:border-accent hover:bg-accent-soft'
                      }`}
                    >
                      <span className="text-sm font-medium">{timeFormat.format(new Date(slot))}</span>
                      {badge && (
                        <span
                          className={`mt-0.5 text-[0.625rem] font-medium leading-none px-1.5 py-0.5 rounded-full ${
                            isSelected
                              ? 'bg-accent-fg/20 text-accent-fg'
                              : isSoldOut
                                ? 'bg-surface text-danger font-semibold'
                                : 'bg-surface-3 text-muted'
                          }`}
                        >
                          {badge}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
